/**
 * dataPreparation.ts - Data Preparation Utilities
 * 
 * CHANGE HISTORY:
 * - 251021_0421 [7529d5e] danielbeer: feat: migrate all packages to TypeScript structure
 */
/**
 * Prepare data for grid
 * @param {Array} data - Raw data
 * @param {Object} options - Options
 * @returns {Array} Prepared data
 */
export function prepareGridData(data, options = {}) {
  if (!data || !Array.isArray(data)) {
    return [];
  }
  
  const { treeData, getDataPath } = options;
  
  if (treeData) {
    return prepareTreeData(data, getDataPath);
  }
  
  return prepareFlatData(data);
}

/**
 * Prepare flat data
 * @param {Array} data - Raw data
 * @returns {Array} Prepared data
 */
function prepareFlatData(data) {
  return data.map((item, index) => ({
    ...item,
    _rowIndex: index,
  }));
}

/**
 * Prepare tree data
 * @param {Array} data - Raw hierarchical data
 * @param {Function} getDataPath - Function to get path from item
 * @returns {Array} Flattened data with paths
 */
function prepareTreeData(data, getDataPath) {
  const flat = [];
  
  function flatten(items, parentPath = []) {
    items.forEach(item => {
      const path = getDataPath 
        ? getDataPath(item)
        : [...parentPath, item.id];
      
      flat.push({
        ...item,
        path: path,
        level: path.length,
        hasChildren: item.children && item.children.length > 0,
      });
      
      if (item.children && item.children.length > 0) {
        flatten(item.children, path);
      }
    });
  }
  
  flatten(data);
  
  return flat;
}

/**
 * Flatten outliner data for grid
 * @param {Array} items - Outliner items
 * @param {Array} parentPath - Parent path
 * @returns {Array} Flattened data
 */
export function flattenOutlinerData(items, parentPath = []) {
  const flat = [];
  
  items.forEach(item => {
    const path = [...parentPath, item.id];
    
    flat.push({
      id: item.id,
      title: item.title,
      description: item.description,
      status: item.status,
      priority: item.priority,
      tags: item.tags,
      createdAt: item.createdAt,
      updatedAt: item.updatedAt,
      path: path,
      level: path.length,
      hasChildren: item.children?.length > 0,
      childCount: item.children?.length || 0,
    });
    
    if (item.children && item.children.length > 0) {
      flat.push(...flattenOutlinerData(item.children, path));
    }
  });
  
  return flat;
}

/**
 * Reconstruct hierarchy from flat grid data
 * @param {Array} flatData - Flat data with paths
 * @returns {Array} Hierarchical data
 */
export function reconstructHierarchy(flatData) {
  const map = new Map();
  const roots = [];
  
  // Create map of all items
  flatData.forEach(item => {
    map.set(item.id, { ...item, children: [] });
  });
  
  // Build hierarchy
  flatData.forEach(item => {
    const node = map.get(item.id);
    
    if (item.path && item.path.length > 1) {
      const parentId = item.path[item.path.length - 2];
      const parent = map.get(parentId);
      
      if (parent) {
        parent.children.push(node);
      } else {
        roots.push(node);
      }
    } else {
      roots.push(node);
    }
  });
  
  return roots;
}

/**
 * Group data by column
 * @param {Array} data - Data to group
 * @param {string} columnKey - Column to group by
 * @returns {Array} Grouped data
 */
export function groupByColumn(data, columnKey) {
  const groups = new Map();
  
  data.forEach(item => {
    const value = item[columnKey];
    const key = value !== null && value !== undefined ? String(value) : '(Empty)';
    
    if (!groups.has(key)) {
      groups.set(key, []);
    }
    
    groups.get(key).push(item);
  });
  
  return Array.from(groups.entries()).map(([key, items]) => ({
    group: key,
    items: items,
    count: items.length,
  }));
}

/**
 * Filter data by search text
 * @param {Array} data - Data to filter
 * @param {string} searchText - Search text
 * @returns {Array} Filtered data
 */
export function filterData(data, searchText) {
  if (!searchText || searchText.trim() === '') {
    return data;
  }
  
  const search = searchText.toLowerCase();
  
  return data.filter(item => {
    return Object.values(item).some(value => {
      if (value === null || value === undefined) return false;
      return String(value).toLowerCase().includes(search);
    });
  });
}

/**
 * Sort data by column
 * @param {Array} data - Data to sort
 * @param {string} columnKey - Column to sort by
 * @param {string} direction - Sort direction ('asc' or 'desc')
 * @returns {Array} Sorted data
 */
export function sortData(data, columnKey, direction = 'asc') {
  const sorted = [...data];
  
  sorted.sort((a, b) => {
    const aVal = a[columnKey];
    const bVal = b[columnKey];
    
    // Handle null/undefined
    if (aVal === null || aVal === undefined) return 1;
    if (bVal === null || bVal === undefined) return -1;
    
    // Compare
    let comparison = 0;
    
    if (typeof aVal === 'number' && typeof bVal === 'number') {
      comparison = aVal - bVal;
    } else if (aVal instanceof Date && bVal instanceof Date) {
      comparison = aVal.getTime() - bVal.getTime();
    } else {
      comparison = String(aVal).localeCompare(String(bVal));
    }
    
    return direction === 'asc' ? comparison : -comparison;
  });
  
  return sorted;
}
